import React, { useState, useEffect } from 'react';
import { Layout, Card, Button, message, Avatar, Dropdown, Modal, ConfigProvider, Divider, Typography, Space } from 'antd';
import { UserOutlined, LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import Login from './components/Login';
import FileManager from './components/FileManager';
import { getUserInfo, clearAuth, isLoggedIn } from './utils/auth';
import { getConfig } from './api/auth';
import './App.css';

const { Header, Content } = Layout;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [checking, setChecking] = useState(true);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [customDomain, setCustomDomain] = useState('');

  useEffect(() => {
    checkAuth();
    loadConfig();
  }, []);

  const checkAuth = async () => {
    const loggedIn = await isLoggedIn();
    if (loggedIn) {
      setIsAuthenticated(true);
      setUserInfo(getUserInfo());
    }
    setChecking(false);
  };

  // 从后端加载配置（环境变量）
  const [apiKeyMask, setApiKeyMask] = useState('');
  const [bucketConfig, setBucketConfig] = useState({ bucket: '', region: '' });

  const loadConfig = async () => {
    try {
      const result = await getConfig();
      if (result.success) {
        if (result.data.customDomain) {
          setCustomDomain(result.data.customDomain);
        }
        setApiKeyMask(result.data.apiKeyMask || '');
        setBucketConfig({
          bucket: result.data.bucket || '',
          region: result.data.region || ''
        });
      }
    } catch (error) {
      console.error('加载配置失败:', error);
    }
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
    setUserInfo(getUserInfo());
  };

  const handleLogout = () => {
    clearAuth();
    setIsAuthenticated(false);
    setUserInfo(null);
    message.success('已退出登录');
  };

  // 用户菜单
  const userMenuItems = [
    {
      key: 'info',
      label: (
        <div>
          <div style={{ fontWeight: 500 }}>{userInfo?.name}</div>
          <div style={{ fontSize: 12, color: '#888' }}>
            权限: {userInfo?.permissions?.join(', ')}
          </div>
        </div>
      ),
      disabled: true
    },
    { type: 'divider' },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
      onClick: () => setSettingsVisible(true)
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: handleLogout
    }
  ];



  if (checking) {
    return (
      <ConfigProvider theme={{ token: { colorPrimary: '#1E90FF' } }}>
        <div className="loading">加载中...</div>
      </ConfigProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <ConfigProvider theme={{ token: { colorPrimary: '#1E90FF' } }}>
        <Login onLogin={handleLogin} />
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#1E90FF' } }}>
    <Layout className="app-layout">
      <Header className="app-header">
        <div className="logo">腾讯云 COS 管理后台</div>
        <div className="header-right">
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Button type="text" className="user-button">
              <Avatar icon={<UserOutlined />} size="small" />
              <span className="username">{userInfo?.name}</span>
            </Button>
          </Dropdown>
        </div>
      </Header>
      <Content className="app-content">
        <Card className="main-card">
          <FileManager userInfo={userInfo} customDomain={customDomain} />
        </Card>
      </Content>

      {/* 设置弹窗 - 显示配置信息 */}
      <Modal
        title="设置与 API 信息"
        open={settingsVisible}
        onCancel={() => setSettingsVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setSettingsVisible(false)}>
            关闭
          </Button>
        ]}
        width={520}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Typography.Text strong>CDN 域名：</Typography.Text>
            <Typography.Text>{customDomain || '未配置（使用 COS 默认域名）'}</Typography.Text>
          </div>

          <Divider style={{ margin: '12px 0' }} />

          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
              API 接口信息（供第三方集成）
            </Typography.Text>

            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 6 }}>
              <Typography.Text copyable style={{ display: 'block', fontSize: 13 }}>
                POST {window.location.origin}/api/sts
              </Typography.Text>

              <div style={{ marginTop: 8 }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Header: </Typography.Text>
                <Typography.Text code style={{ fontSize: 12 }}>
                  X-API-Key: {apiKeyMask || '未配置'}
                </Typography.Text>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>cURL 示例：</Typography.Text>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: 10,
                  borderRadius: 6,
                  fontSize: 11,
                  overflow: 'auto',
                  marginTop: 4,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  const text = `curl -X POST "${window.location.origin}/api/sts" \\\n  -H "X-API-Key: ${apiKeyMask || '你的API密钥'}"`;
                  navigator.clipboard.writeText(text);
                  message.success('已复制到剪贴板');
                }}
              >
{`curl -X POST "${window.location.origin}/api/sts" \\
  -H "X-API-Key: ${apiKeyMask || '你的API密钥'}"`}
              </pre>
            </div>

            <Typography.Text type="warning" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              提示：API Key 请在 EdgeOne Pages 控制台环境变量中查看完整值（API_SECRET_KEY）
            </Typography.Text>
          </div>
        </Space>
      </Modal>
    </Layout>
    </ConfigProvider>
  );
}

export default App;
